//#region Imports and Global Definitions
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StyleSheet, View, Modal, SafeAreaView, Image, Text, Platform, useWindowDimensions, Pressable, FlatList } from 'react-native';
import { useTheme } from '@react-navigation/native';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomButton from '../../../components/CustomButton';
import CustomDropDown from '../../../components/CustomDropDown/CustomDropdown';
import { createCommonStyles } from '../../../styles/commonStyles';
import { ActivityIndicator, Card } from 'react-native-paper';
import { showMessage } from "react-native-flash-message";
import { useTableHelper } from '../../../hooks/useTableHelper';
import { useTranslation } from 'react-i18next';
import { useTraining } from '../../../hooks/useTraining';
import { generateUniqueID } from '../../../components/SurveyBuilderComponent/CreateSurvey/CreateSurveyFunctions';
//#endregion

const PrerequisitesListView = React.memo(({ item, index, styles, handleEditPress, handleDelete, trnOptions
}) => {
    return (
        <SafeAreaView>
            <SafeAreaView style={styles.row}>
                <View style={[styles.item]}>
                     <CustomDropDown
                        defValue={item.trnUI}
                        options={trnOptions}
                        value={item.trnUI}
                        setValue={(e) => { handleEditPress(index, item, 'trnUI', e)}}
                        type="ASSESSMENT"
                    />
                </View>
                <CustomButton
                    iconName='trash-outline'
                    onPress={() => { handleDelete(item, index, 'rowguid') }}
                    show='HOME_BELL'
                    type='HOME_BELL'
                    textTop='2'
                    sz={30} />
            </SafeAreaView>
        </SafeAreaView>
    );
});

const HRAdminPrerequisitesScreen = (props) => {
    //#region Props
    const { trnUI, trnData, isEdit } = props
    //#endregion

    //#region Const Definitions
    const ref = useRef(null);
    const { t, i18n } = useTranslation();
    const { width, height } = useWindowDimensions();
    const isPlatformWeb = (Platform.OS === 'web' && width > 992 ? false : true);
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const [isLoading, setIsLoading] = useState(true);

    const { loadPrerequisites, deletePrerequisites, SavePrerequisites, loadValidationFlags } = useTraining();
    const [refreshAfterSave, setRefreshAfterSave] = useState(false);
    const { rowEdit, rowDelete, rowAdd, handleCancelChanges, hasDuplicates } = useTableHelper();

    const [prerequisitesData, setPrerequisitesData] = useState([]);
    const [changes, setChanges] = useState({});
    const [insertData, setInsertData] = useState([]);
    const [deletedItems, setDeletedItems] = useState([]);
    const [trnOptions, setTRNOptions] = useState([]);

    useEffect(() => {
        setIsLoading(true);
        const filteredOptions = trnData.map(item => {
            return {
                value: item.trnUI,
                label: item.title
            };
        });
        setTRNOptions(filteredOptions);
        if(isEdit){
            loadPrerequisites(trnUI).then((pre) => {
                setPrerequisitesData(pre);
                setRefreshAfterSave(false);
                setChanges({});
                setInsertData([]);
                setDeletedItems([]);
                setIsLoading(false);
            })
            .catch(() => {
                setRefreshAfterSave(false);
                setIsLoading(false);
            });
        }
        setRefreshAfterSave(false);
        setIsLoading(false);
    }, [refreshAfterSave])

    //#region Components
    const renderData = (item, index) => {
        return (
            <PrerequisitesListView
                item={item}
                index={index}
                styles={styles}
                handleEditPress={handleEditPress}
                handleDelete={handleDelete}
                trnOptions={trnOptions}
            />
        )
    }

    const renderPrerequisites = useCallback((item, index) => {
        return (
            <Card
                style={[common.cardstyle, { margin: 10, zIndex: -1 }]} >
                <Card.Content>
                    {renderData(item, index)}
                </Card.Content>
            </Card >
        )
    }, [prerequisitesData]);
    //#endregion

    //#region Handlers
    const handleEditPress = useCallback((index, item, key, value) => {
        if (item[key] === value) return;
        rowEdit(index, item, key, value, changes, setChanges, prerequisitesData, setPrerequisitesData)
    }, []);

    const handleDelete = (item, index, attribute) => {
        rowDelete(item, index, attribute, changes, setChanges, prerequisitesData, setPrerequisitesData, setDeletedItems, ref)
    }

    // Update handleAddNew to add new prerequisite item with correct keys
    const handleAddNew = () => {
        let temp = [...prerequisitesData];
        if (prerequisitesData.length === 0)
            temp = {
                trnUI: "",
                rowguid: generateUniqueID()
            }
        rowAdd(temp, setPrerequisitesData, ['rowguid'], [], ref, t, [], [], undefined, undefined, insertData, setInsertData, id = 'rowguid',);
    }

    const removeFromChangesAfterSave = () => {
        setChanges([])
        setDeletedItems([])
    }

    //#region SavePrerequisite
    const handleSave = async () => {
        if (Object.entries(changes).length === 0 && deletedItems.length === 0 && insertData.length === 0) {
            showMessage({
                message: t('common:noChanges'),
                type: "warning",
                duration: 3000,
            });
            return;
        }

        // Validate required fields
        for (const mod of prerequisitesData) {
            if (!mod.trnUI) {
                showMessage({
                    message: t('common:trnEmpty'),
                    type: "danger",
                    duration: 3000,
                });
                return;
            }
        }

        // Validate prerequisitesData
        try {
            const res = await loadValidationFlags(prerequisitesData);
            for (const validation of res) {
                const { post, pre, twoPre } = validation;

                // Check if the validation condition is met for the current training
                if ((post && pre) || (!post && twoPre)) {
                    showMessage({
                        message: t('common:validationMssg'),
                        type: "danger",
                        duration: 4000,
                    });
                    return; 
                }
            }
        } catch (err) {
            showMessage({
                message: t('common:validationErr'),
                type: "danger",
                duration: 3000,
            });
        }

        // Check for duplicate prerequisites
        const trnUIs = prerequisitesData.map(p => p.trnUI);
        const uniqueTrnUIs = new Set(trnUIs);

        if (uniqueTrnUIs.size < trnUIs.length) {
            showMessage({
                message: t('common:dublicatedTRN'),
                type: "danger",
                duration: 3000,
            });
            return; 
        }

        setIsLoading(true);

        // Prepare data for insert and update
        const dataForInsert = [];
        const dataForUpdate = [];
        const insertSuccess = true;
        const updateSuccess = true;
        const deleteSuccess = true;
        const promises = [];

        prerequisitesData.forEach(mod => {
            if (insertData.includes(mod.rowguid)) {
                dataForInsert.push(mod);
            } else {
                dataForUpdate.push(mod);
            }
        });

        dataForInsert.forEach(item => {
            if (!item.rowguid) {
                item.rowguid = generateUniqueID();
            }
        });

        if (dataForInsert.length > 0) {
            promises.push(
                SavePrerequisites(dataForInsert, trnUI)
                    .then(res => {
                        if (res !== 200)
                            insertSuccess = false;
                    })
            );
        }

        if (dataForUpdate.length > 0) {
            promises.push(
                SavePrerequisites(dataForUpdate)
                    .then(res => {
                        if (res !== 200)
                            updateSuccess = false;
                    })
            );
        }

        if (deletedItems.length > 0) {
            promises.push(
                deletePrerequisites(deletedItems)
                    .then(res => {
                        if (res !== 200)
                            deleteSuccess = false;
                    })
            );
        }

        try {
            await Promise.all(promises);
            if (updateSuccess && insertSuccess && deleteSuccess) {
                showMessage({
                    message: t('common:allOperationsSuccMess'),
                    type: "success",
                    duration: 3000,
                });
                setRefreshAfterSave(true);
                removeFromChangesAfterSave();
            } else {
                showMessage({
                    message: t('common:saveFailed'),
                    type: "danger",
                    duration: 3000,
                });
            }
        } catch (error) {
            showMessage({
                message: t('common:saveFailed'),
                type: "danger",
                duration: 3000,
            });
        } finally {
            setIsLoading(false);
        }
    };
    //#endregion

    //#region Loading
    if (isLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )
    //#endregion

    return (
        <SafeAreaView style={styles.innerView}>
            <SafeAreaView style={[styles.buttons, styles.functionalityButtons]}>
                <CustomButton
                    text={t('common:new')}
                    onPress={() => { handleAddNew() }}
                    type='BUTTON_ADD'
                    textTop='0'
                    sz={15}
                />
                <CustomButton
                    text={t('common:save')}
                    onPress={() => { handleSave() }}
                    type='BUTTON_SAVE'
                    textTop='0'
                    sz={15}
                    isGradient={true}
                />
                <CustomButton
                    text={t('common:cancel')}
                    onPress={() => { setRefreshAfterSave(true) }}
                    type="BUTTON_CANCEL"
                    textTop='0'
                    sz={15}
                />
            </SafeAreaView>
            <FlatList
                data={prerequisitesData}
                keyExtractor={(item) => item.rowguid}
                scrollEnabled={true}
                initialNumToRender={4}
                renderItem={({ item, index }) =>
                    renderPrerequisites(item, index)
                }
                ListHeaderComponent={
                    <SafeAreaView style={common.listTitle}>
                        <SafeAreaView style={[styles.rowHeaders]}>
                            <CustomLabel
                                value={t('common:training')}
                                type='SUBTITLE' />
                        </SafeAreaView>
                        <SafeAreaView style={common.divider} />
                    </SafeAreaView>
                }
                ListEmptyComponent={
                    isLoading ?
                        <ActivityIndicator animating={true} color={colors.blue} />
                        :
                        <CustomLabel
                            value={t("common:NoDataPreview")}
                            type="NO_FUNCTIONS"
                        />
                }
            />
        </SafeAreaView>
    );
};

//#region Styles
const makeStyles = (colors, width) => StyleSheet.create({
    mainView: {
        flex: 1,
    },
    subMain: {
        borderRadius: 35,
        elevation: (Platform.OS === 'web' && width > 992 ? 3 : 0),
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
        width: '100%'
    },
    rowView: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center'
    },
    cardStyleFunctions: {
        margin: 10,
        borderRadius: 5,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
    },
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
    },
    item: {
        flex: 1,
    },
    error: {
        color: colors.validation_error,
        alignSelf: 'center',
        fontSize: 14,
    },
    selectedButton: {
        margin: 10,
        borderRadius: 5,
        borderWidth: 2,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
        borderColor: colors.buttons_submit,
    },
    backButton: {
        alignSelf: 'flex-end',
        margin: 0
    },
    innerView: {
        flex: 1,
    },
    functionsView: {
        flexDirection: 'row',
        height: '100%'
    },
    fullHeight: {
        height: '95%',
    },
    fullWidth: {
        height: '85%',
    },
    servLinesView: {
        width: '85%',
        height: '100%',
    },
    functionalityButtons: {
        justifyContent: 'flex-end'
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        width: '50%',
        alignSelf: 'flex-end',
    },
    loader: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '4%'
    },
    listTitle: {
        flex: 1,
        marginVertical: 10,
    },
    rowHeaders: {
        flexDirection: "row",
        marginHorizontal: '1%',
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContainer: {
        backgroundColor: colors.background,
        borderRadius: 10,
        width: '80%',
        height: '70%',
        padding: 20,
    },
    errorMessageContainer: {
        backgroundColor: colors.validation_error,
        padding: 10,
        borderRadius: 5,
        marginBottom: 10,
    },
    errorMessageText: {
        color: 'white',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    attendanceContent: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    columnLeft: {
        flex: 4,
        paddingRight: 5,
    },
    columnMiddle: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: '5%'
    },
    columnRight: {
        flex: 4,
        marginLeft: 5,
    },
    listItem: {
        paddingVertical: 8,
        paddingHorizontal: 10,
        borderBottomWidth: 1,
        borderBottomColor: colors.card_line,
    },
    modalButtonsContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        paddingHorizontal: 20,
        paddingVertical: 15,
        marginTop: 10,
    },
    employeeContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        // justifyContent: 'center',
        marginVertical: 3,
    },
    employeePhotograph: {
        width: 30,
        height: 30,
        marginLeft: 10,
        borderRadius: 100,
        borderWidth: 1,
        verticalAlign: 'middle',
    },
});
//#endregion

export default HRAdminPrerequisitesScreen;